/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/DialRuleRow.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Sortable row component for Dial Rules with drag & drop support.
────────────────────────────────────────────────────────────────*/

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, GripVertical } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { RuleFormState } from '../types';
import { useI18n } from '../../../hooks/useI18n';

interface DialRuleRowProps {
  rule: RuleFormState;
  index: number;
  onUpdate: (index: number, field: keyof RuleFormState, value: unknown) => void;
  onRemove: (index: number) => void;
}

export function DialRuleRow({ rule, index, onUpdate, onRemove }: DialRuleRowProps) {
  const { t } = useI18n();
  const sortable = useSortable({ id: rule.clientId });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  } as React.CSSProperties;

  return (
    <tr ref={sortable.setNodeRef} style={style} className="border-b border-border hover:bg-muted/50">
      <td className="py-3 px-2 text-center w-[5%]">
        <SimpleTooltip content={t('dialRuleRestrictions.tooltips.moveRule')}>
          <div
            {...sortable.attributes}
            {...sortable.listeners}
            className="cursor-grab hover:cursor-grabbing p-2 text-muted-foreground hover:text-foreground rounded hover:bg-muted/50"
          >
            <GripVertical className="h-4 w-4" />
          </div>
        </SimpleTooltip>
      </td>
      <td className="py-3 px-4 text-center w-[12%]">
        <select
          value={rule.direction}
          onChange={(event) =>
            onUpdate(index, 'direction', event.target.value as RuleFormState['direction'])
          }
          className="w-full px-2 py-1 border border-border rounded text-xs bg-background text-foreground"
          aria-label={t('dialRuleRestrictions.type')}
        >
          <option value="outbound">Outbound</option>
          <option value="internal">Internal</option>
        </select>
      </td>
      <td className="py-3 px-4 w-[23%]">
        <Input
          value={rule.pattern}
          onChange={(event) => onUpdate(index, 'pattern', event.target.value)}
          placeholder={t('dialRuleRestrictions.ruleFields.pattern.placeholder')}
          aria-label={t('dialRuleRestrictions.ruleFields.pattern.label')}
        />
      </td>
      <td className="py-3 px-4 text-center w-[10%]">
        <Toggle
          value={rule.allowed}
          onChange={(value) => onUpdate(index, 'allowed', value)}
          aria-label={t('dialRuleRestrictions.ruleFields.allowed.label')}
        />
      </td>
      <td className="py-3 px-4 w-[18%]">
        <Input
          value={rule.announcement ?? ''}
          onChange={(event) => onUpdate(index, 'announcement', event.target.value)}
          placeholder={t('dialRuleRestrictions.ruleFields.announcement.placeholder')}
        />
      </td>
      <td className="py-3 px-4 text-center w-[12%]">
        <Toggle
          value={rule.playMaxDuration}
          onChange={(value) => onUpdate(index, 'playMaxDuration', value)}
          aria-label={t('dialRuleRestrictions.ruleFields.playMaxDuration.label')}
        />
      </td>
      <td className="py-3 px-4 text-center w-[12%]">
        {rule.playMaxDuration ? (
          <Input
            type="number"
            value={rule.maxDurationSeconds ?? ''}
            onChange={(event) =>
              onUpdate(index, 'maxDurationSeconds', Number(event.target.value) || null)
            }
            placeholder="120"
            aria-label={t('dialRuleRestrictions.ruleFields.maxDurationSeconds.label')}
            className="text-center"
          />
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-center w-[12%]">
        <Toggle
          value={rule.requirePassword}
          onChange={(value) => onUpdate(index, 'requirePassword', value)}
          aria-label={t('dialRuleRestrictions.ruleFields.requirePassword.label')}
        />
      </td>
      <td className="py-3 px-4 text-center w-[8%]">
        <SimpleTooltip content={t('dialRuleRestrictions.tooltips.deleteRule')}>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </td>
    </tr>
  );
}

